import { MSG_NAME_FROM_IFRAME, sendMessageFromIframe } from '@src/container/handleIframeMessage/handleIframeMessage';
import {
  ChangeTestEventData,
  ChangeVariationEventData,
  ForceTestTargetingEventData,
} from '@container/handleIframeMessage/types';
import { QATest } from '@src/application/modules/tag/shared/tag/types';
import type { TFunction } from 'i18next';
import { isStatusAcceptedOrForced } from '../shared/tag/helpers';
import { useTranslation } from 'react-i18next';
import { FavoriteUrlOperator } from '@abtasty/api-client';
import { FrequencyType, TestStatus, TestStatusRejected } from '../shared/tag/serializedTagTypes';
import {
  CLASS_SELECTOR,
  CONTAINS,
  EQUALS,
  ID_SELECTOR,
  IGNORE_PARAMETERS,
  REGEXP,
  SAVED_URLS,
} from '@abtasty/api-client/dist/endpoints/audiences/types/conditions';

export const changeVariation = (testId: number, initialVariation: number, value: number, status: QATest['status']) => {
  if (initialVariation !== value) {
    const data: ChangeVariationEventData = {
      name: MSG_NAME_FROM_IFRAME.QaAssistantChangeVariation,
      testId,
      value,
      shouldReload: isStatusAcceptedOrForced(status),
    };
    sendMessageFromIframe(data);
  }
};

export const changeTest = (testId: number, initialTest: number, value: number) => {
  if (initialTest !== value) {
    const data: ChangeTestEventData = {
      name: MSG_NAME_FROM_IFRAME.QaAssistantChangeTest,
      testId,
      value,
    };
    sendMessageFromIframe(data);
  }
};

export function forceTestTargeting(testId: number, action: ForceTestTargetingEventData['action']) {
  const data: ForceTestTargetingEventData = {
    name: MSG_NAME_FROM_IFRAME.QaAssistantEventForceTestTargeting,
    testId,
    action,
  };
  sendMessageFromIframe(data);
}

export const getUrlOperatorLabel = (id: number, include: boolean, t: TFunction): string => {
  switch (id) {
    case IGNORE_PARAMETERS:
      return include ? t('Is') : t('Is not');
    case EQUALS:
      return include ? t('Is exactly') : t('Is not exactly');
    case CONTAINS:
      return include ? t('Contains') : t('Does not contain');
    case REGEXP:
      return include ? t('Is regular expression') : t('Is not regular expression');
    case SAVED_URLS:
      return include ? t('Is saved URL(s)') : t('Is not saved URL(s)');
    default:
      return '';
  }
};

export const useFavoriteUrlOperatorLabel = () => {
  const { t } = useTranslation();
  const includeOperators = {
    [FavoriteUrlOperator.is]: t('is'),
    [FavoriteUrlOperator['is strictly']]: t('is strictly'),
    [FavoriteUrlOperator.contain]: t('contains'),
    [FavoriteUrlOperator.regex]: t('matches regex'),
  };
  const excludeOperators = {
    [FavoriteUrlOperator.is]: t('is not'),
    [FavoriteUrlOperator['is strictly']]: t('is not strictly'),
    [FavoriteUrlOperator.contain]: t('does not contain'),
    [FavoriteUrlOperator.regex]: t('does not match regex'),
  };
  return (include: boolean, operator: FavoriteUrlOperator) =>
    include ? includeOperators[operator] : excludeOperators[operator];
};

export const getSelectorLabel = (id: number, t: TFunction): string => {
  switch (id) {
    case ID_SELECTOR:
      return t('ID');
    case CLASS_SELECTOR:
      return t('Class');
    default:
      return '';
  }
};

export const getTargetingLabel = (name: string, t: TFunction): string => {
  switch (name) {
    case 'EULERIAN_DMP':
      return t('Eulerian DMP');
    case 'TAGCOMMANDER_DMP':
      return t('Tagcommander DMP');
    case 'BLUEKAI_DMP':
      return t('Bluekai DMP');
    case 'YSANCE_DMP':
      return t('Ysance DMP');
    case 'ADVALO_DMP':
      return t('Advalo DMP');
    case 'KRUX_DMP':
      return t('Krux DMP');
    case 'WEBORAMA_DMP':
      return t('Weborama DMP');
    case 'TEMELIO_DMP':
      return t('Temelio DMP');
    case 'ADOBE_DMP':
      return t('Adobe DMP');
    case 'LIVERAMP_DMP':
      return t('Liveramp DMP');
    case 'MEDIARITHMICS_DMP':
      return t('Mediarithmics DMP');
    case 'LEROYMERLIN_DMP':
      return t('Leroymerlin DMP');
    case 'SIRDATA_DMP':
      return t('Sirdata DMP');
    case 'DEVICE':
      return t('Device');
    case 'IP':
      return t('IP');
    case 'GEOLOCALISATION':
      return t('Geolocation');
    case 'COOKIE':
      return t('Cookie');
    case 'BROWSER_LANGUAGE':
      return t('Browser language');
    case 'LANDING_PAGE':
      return t('Landing page');
    case 'BROWSER':
      return t('Browser');
    case 'RETURNING_VISITOR':
      return t('Returning visitor');
    case 'SOURCE_TYPE':
      return t('Source type');
    case 'PREVIOUS_PAGE':
      return t('Previous page');
    case 'SCREEN_SIZE':
      return t('Screen size');
    case 'JS_VARIABLE':
      return t('JS variable');
    case 'CAMPAIGN_EXPOSITION':
      return t('Campaign exposition');
    case 'SOURCE':
      return t('Source');
    case 'NUMBER_PAGES_VIEWED':
      return t('Number pages viewed');
    case 'SAME_DAY_VISIT':
      return t('Same day visit');
    case 'ADBLOCK':
      return t('Adblock');
    case 'SESSION_NUMBER':
      return t('Session number');
    case 'WEATHER':
      return t('Weather');
    case 'DAYS_SINCE_LAST_SESSION':
      return t('Days since last session');
    case 'DAYS_SINCE_FIRST_SESSION':
      return t('Days since first session');
    case 'ECOMMERCE_VARIABLE':
      return t('Ecommerce variable');
    case 'URL_PARAMETER':
      return t('URL parameter');
    case 'CODE':
      return t('Code');
    case 'CUSTOM_VARIABLE':
      return t('Custom variable');
    case 'ACTION_TRACKING':
      return t('Action tracking');
    case 'SELECTOR':
      return t('Selector');
    case 'DATALAYER':
      return t('Datalayer');
    case 'LAST_PURCHASE':
      return t('Lastpurchase');
    case 'PURCHASE_FREQUENCY':
      return t('Purchase frequency');
    case 'KEYWORD':
      return t('Keyword');
    case 'TEALIUM':
      return t('Tealium');
    case 'ENGAGEMENT_LEVEL':
      return t('Engagement level');
    case 'CONTENT_INTEREST':
      return t('Content interest');
    case 'PAGE_VIEW':
      return t('Page view');
    case 'PAGES_INTEREST':
      return t('Pages interest');
    case 'PROVIDERS':
      return t('Providers');
    case 'ABANDONED_CART':
      return t('Abandoned cart');
    default:
      return name;
  }
};

export const getDisplayFrequencyTypeText = (type: FrequencyType, t: TFunction) => {
  switch (type) {
    case FrequencyType.any:
      return t('any time', { context: 'display_frequency' });
    case FrequencyType.oncePerSession:
      return t('once per session', { context: 'display_frequency' });
    default:
      return type || '';
  }
};

const campaignStatusDisablingForceDisplayButton: Record<string, boolean> = {
  [TestStatusRejected.anotherSubsegmentAlreadyStarted]: true,
  [TestStatusRejected.timeout]: true,
  [TestStatusRejected.deferredFailedLoading]: true,
  [TestStatusRejected.notPrioritizedYet]: true,
};

export function isForceDisplayButtonDisabled(status: TestStatus): boolean {
  return campaignStatusDisablingForceDisplayButton[status] || false;
}
